import type { PaginationModel } from '@montugroup/design-system';
import { PaginationVariant, Table } from '@montugroup/design-system';
import { Box, styled, useTheme } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';

import { useAccessControl } from '@/hooks';
import { ConsultationTableColumnHeaders, UserRole } from '@/types';

import FetchFailedError from '@/error-handling/components/FetchFailedError';
import { useMemo } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../Patient/Patient.constants';
import type { ConsultationWithPaymentStatus } from './hooks/useConsultationTabOverviewTable';

// TODO: Remove this once the column header minWidth is removed from the <Table> in upcoming refactor
const StyledBox = styled(Box)({
  'thead > tr > th': {
    minWidth: 'inherit',
  },
});

type ConsultationTabOverviewTableProps = {
  consultations: ConsultationWithPaymentStatus[];
  isLoadingConsultations?: boolean;
  isErrorConsultations?: boolean;
  searchFilterText?: string;
  responseCursors: any;
  handlePaginationChange: (paginationModel: PaginationModel) => void;
  onSort?: (sort: { id: string; desc: boolean }[]) => void;
  isProcessingConsultations?: boolean;
  disabledConsultsCodes?: ConsultationTableColumnHeaders[];
  scheduledConsultId?: number;
  disableShadow?: boolean;
  hidden?: boolean;
};

/**
 * ConsultationTabOverviewTable component
 *
 * @remarks
 * Renders a table of consultations in the Consultation Tab on the Patient page
 *
 * @param consultations - An array of consultations
 * @param isLoading - isLoading state from the Patient profile
 * @param searchFilterText - The search filter text
 *
 * @returns JSXElement
 *
 */

export const ConsultationTabOverviewTable = ({
  consultations,
  isLoadingConsultations,
  isErrorConsultations,
  searchFilterText,
  handlePaginationChange,
  responseCursors,
  onSort,
  isProcessingConsultations = false,
  disabledConsultsCodes = [],
  scheduledConsultId,
  disableShadow,
  hidden,
}: ConsultationTabOverviewTableProps) => {
  const theme = useTheme();

  const columnHelper = createColumnHelper();
  const { restrictAccess } = useAccessControl();

  const isLoading = isLoadingConsultations || isProcessingConsultations;

  const columns: any = [
    columnHelper.accessor('view_link', {
      header: ConsultationTableColumnHeaders.ViewLink,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('consultation_code', {
      header: ConsultationTableColumnHeaders.ConsultID,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('appointment_time', {
      header: ConsultationTableColumnHeaders.AppointmentTime,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: true,
    }),
    columnHelper.accessor('type', {
      header: ConsultationTableColumnHeaders.ConsultationType,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('practitioner', {
      header: ConsultationTableColumnHeaders.Practitioner,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('payment_status', {
      header: ConsultationTableColumnHeaders.PaymentStatus,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('consultation_status', {
      header: ConsultationTableColumnHeaders.ConsultationStatus,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('payment_type', {
      header: ConsultationTableColumnHeaders.PaymentType,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('reschedule_link', {
      header: ConsultationTableColumnHeaders.RescheduleLink,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
    columnHelper.accessor('cancel_link', {
      header: ConsultationTableColumnHeaders.CancelLink,
      cell: (cell) => {
        return cell.getValue() || '-';
      },
      enableSorting: false,
    }),
  ];

  const rescheduleConsultationAccessId = restrictAccess([UserRole.Doctor], 'Reschedule');
  const cancelConsultationAccessId = restrictAccess([UserRole.Doctor], 'Cancel');
  const editConsultationAccessId = restrictAccess([UserRole.Admin], 'View');

  const noDataFoundMessage = isLoading ? 'Loading...' : 'No consultations found';

  const columnVisibility = useMemo(
    () => ({
      view_link: Boolean(editConsultationAccessId),
      reschedule_link:
        Boolean(rescheduleConsultationAccessId) &&
        !disabledConsultsCodes.includes(ConsultationTableColumnHeaders.RescheduleLink),
      cancel_link:
        Boolean(cancelConsultationAccessId) &&
        !disabledConsultsCodes.includes(ConsultationTableColumnHeaders.CancelLink),
      payment_status: !disabledConsultsCodes.includes(ConsultationTableColumnHeaders.PaymentStatus),
      payment_type: !disabledConsultsCodes.includes(ConsultationTableColumnHeaders.PaymentType),
    }),
    [editConsultationAccessId, rescheduleConsultationAccessId, cancelConsultationAccessId, disabledConsultsCodes],
  );

  const applyRowStyles = useMemo<
    (row: { original: ConsultationWithPaymentStatus } | unknown) => Record<string, unknown>
  >(() => {
    return (row) => {
      if (!scheduledConsultId) {
        return {};
      }
      if (
        typeof row === 'object' &&
        row !== null &&
        'original' in row &&
        (row.original as ConsultationWithPaymentStatus).consultationId === scheduledConsultId
      ) {
        return {
          opacity: 0.8,
          pointerEvents: 'none',
          '& *': {
            color: `${theme.palette.action.disabled} !important`,
          },
        };
      }
      return {};
    };
  }, [scheduledConsultId, theme]);

  if (isErrorConsultations) {
    return <FetchFailedError />;
  }
  return (
    <StyledBox
      sx={{
        display: hidden ? 'none' : undefined,
      }}
    >
      <Table
        data={consultations}
        columns={columns}
        skeletonCount={2}
        pageSize={DEFAULT_PAGE_SIZE}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        globalFilter={searchFilterText}
        noDataFoundMessage={noDataFoundMessage}
        showPagination
        onPaginationModelChange={handlePaginationChange}
        columnVisibility={columnVisibility}
        onSortingChange={onSort}
        paginationVariant={PaginationVariant.UNKNOWN_PAGE_SIZE}
        hasPrevPage={Boolean(responseCursors?.prev)}
        hasNextPage={Boolean(responseCursors?.next)}
        applyRowStyles={applyRowStyles}
        elevation={disableShadow ? 0 : undefined}
      />
    </StyledBox>
  );
};
